import { create } from 'zustand';

/** One editor's share of the save bar: whether it has edits and how to save or drop them. */
export interface SaveBarEntry {
  id: string;
  dirty: boolean;
  /** A save is running (the form's own submit or the mutation behind it). */
  saving: boolean;
  /** Runs the editor's submit path, validation included. Errors stay the editor's to render. */
  save: () => unknown;
  /** Puts the editor back to its last saved values. */
  discard: () => void;
  /** Ask "Discard all unsaved changes?" before discarding. Off by default. */
  confirmDiscard: boolean;
}

export interface SaveBarState {
  entries: Record<string, SaveBarEntry>;
  /** Saves the bar itself started and is still waiting for. */
  pendingSaves: number;
  /** Bumped every time a navigation is refused; the bar shakes on each change. */
  shakeCount: number;
  /** When set, navigation passes although an entry is dirty (its record was deleted). */
  released: boolean;
  register: (entry: SaveBarEntry) => void;
  update: (id: string, patch: Partial<Omit<SaveBarEntry, 'id'>>) => void;
  unregister: (id: string) => void;
  shake: () => void;
  release: () => void;
  beginSave: () => void;
  endSave: () => void;
}

/**
 * Client-local registry behind the save bar. Editors register through `useSaveBar` or
 * `useSaveBarEntry`; the bar in the top bar reads the union. One store per page, not per form,
 * so a page with two editors shows one bar whose Save and Discard act on both.
 */
export const useSaveBarStore = create<SaveBarState>()((set) => ({
  entries: {},
  pendingSaves: 0,
  shakeCount: 0,
  released: false,
  register: (entry) =>
    set((state) => ({ entries: { ...state.entries, [entry.id]: entry }, released: false })),
  update: (id, patch) =>
    set((state) => {
      const current = state.entries[id];
      if (!current) return state;
      return { entries: { ...state.entries, [id]: { ...current, ...patch } } };
    }),
  unregister: (id) =>
    set((state) => {
      if (!(id in state.entries)) return state;
      const entries = Object.fromEntries(
        Object.entries(state.entries).filter(([key]) => key !== id),
      );
      // The release was for the editor that just left; the next editor starts guarded again.
      const released = Object.keys(entries).length === 0 ? false : state.released;
      return { entries, released };
    }),
  shake: () => set((state) => ({ shakeCount: state.shakeCount + 1 })),
  release: () => set({ released: true }),
  beginSave: () => set((state) => ({ pendingSaves: state.pendingSaves + 1 })),
  endSave: () => set((state) => ({ pendingSaves: Math.max(0, state.pendingSaves - 1) })),
}));

export function isSaveBarDirty(state: Pick<SaveBarState, 'entries'>): boolean {
  return Object.values(state.entries).some((entry) => entry.dirty);
}

/**
 * True while any editor reports a save of its own (Enter in the form, a page button) or while
 * a save the bar started has not settled. The second half is what lets a save that navigates
 * (create, then open the record) pass the guard before the editor's state has caught up.
 */
export function isSaveBarSaving(state: Pick<SaveBarState, 'entries' | 'pendingSaves'>): boolean {
  return state.pendingSaves > 0 || Object.values(state.entries).some((entry) => entry.saving);
}

/** Dirty entries only: the ones Save submits and Discard resets. */
export function dirtyEntries(state: Pick<SaveBarState, 'entries'>): SaveBarEntry[] {
  return Object.values(state.entries).filter((entry) => entry.dirty);
}

/** Whether a navigation from `from` to `to` must wait for the person to save or discard. */
export function shouldGuardNavigation(
  state: Pick<SaveBarState, 'entries' | 'pendingSaves' | 'released'>,
  from: { pathname: string },
  to: { pathname: string },
): boolean {
  if (state.released) return false;
  if (!isSaveBarDirty(state) || isSaveBarSaving(state)) return false;
  // Search-only changes (a `?tab=` switch) keep the page and its editors mounted.
  return from.pathname !== to.pathname;
}

/**
 * Lets the next navigation through although an editor is dirty, from outside the editor: a
 * danger zone that deletes the record and then leaves. Same as the `release()` the hooks return.
 */
export function releaseSaveBar(): void {
  useSaveBarStore.getState().release();
}

/**
 * Saves every dirty editor on the page: what the bar's Save and Ctrl+S do, and what a page's
 * own Save button calls so it writes the gallery next to the form.
 */
export async function saveDirtyEditors(): Promise<void> {
  const state = useSaveBarStore.getState();
  const entries = dirtyEntries(state);
  state.beginSave();
  try {
    // Started synchronously, inside the click or keydown, so the editors' own submitting state
    // lands in React's flush of this event. Each editor renders its own failure; the bar only
    // waits for them to settle.
    const started = entries.map((entry) => {
      try {
        return Promise.resolve(entry.save());
      } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
    await Promise.allSettled(started);
  } finally {
    useSaveBarStore.getState().endSave();
  }
}

/** Puts every dirty editor back to its last saved values, without asking. */
export function discardDirtyEditors(): void {
  for (const entry of dirtyEntries(useSaveBarStore.getState())) entry.discard();
}
